/** An amount as the API sends it: minor units and the currency (docs/02 §5). */
export interface Money {
  minor: number;
  currency: string;
}

/** A lowest and highest amount. */
export interface MoneyRange {
  min: Money;
  max: Money;
}

const locales: Record<string, string> = { en: 'en-GB', et: 'et-EE' };

function localeOf(language: string): string {
  return locales[language] ?? 'en-GB';
}

/**
 * ISO 4217's decimals where the browser shows fewer (the rupiah and the kip without cents): the
 * server counts minor units by ISO (`crates/trippath-core/data/currencies.tsv`), and so does
 * this. A test checks every currency of that file against [`digitsOf`].
 */
const iso: Record<string, number> = {
  AFN: 2,
  ALL: 2,
  COP: 2,
  HUF: 2,
  IDR: 2,
  IQD: 3,
  IRR: 2,
  KPW: 2,
  LAK: 2,
  LBP: 2,
  MGA: 2,
  MMK: 2,
  PKR: 2,
  SOS: 2,
  SYP: 2,
  YER: 2,
};

/** How many decimals a currency has: 2 for EUR, 0 for JPY, as ISO 4217 counts them. */
export function digitsOf(currency: string): number {
  const known = iso[currency];
  if (known !== undefined) return known;
  const fraction = new Intl.NumberFormat('en', { style: 'currency', currency })
    .formatToParts(1)
    .find((part) => part.type === 'fraction');
  return fraction?.value.length ?? 0;
}

/**
 * An amount as people read it: `€8,000`, `€48.85`, `8000 €` in Estonian. Whole amounts drop
 * their cents unless `cents` asks to keep them.
 */
export function formatMoney(money: Money, language: string, cents = false): string {
  const digits = digitsOf(money.currency);
  const value = money.minor / 10 ** digits;
  const whole = money.minor % 10 ** digits === 0 && !cents;
  return new Intl.NumberFormat(localeOf(language), {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: whole ? 0 : digits,
    maximumFractionDigits: whole ? 0 : digits,
  }).format(value);
}

/** `€7,450`: rounded to whole units, for figures read at a glance (the mockups' totals). */
export function formatWhole(money: Money, language: string): string {
  const digits = digitsOf(money.currency);
  return new Intl.NumberFormat(localeOf(language), {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
  }).format(Math.round(money.minor / 10 ** digits));
}

/** `€18–35`: a range in one piece, the same amount once. */
export function formatRange(range: MoneyRange, language: string, whole = false): string {
  const format = whole ? formatWhole : formatMoney;
  const min = format(range.min, language);
  const max = format(range.max, language);
  if (min === max) return min;
  const symbol = currencySymbol(range.min.currency, language);
  const number = (money: Money) => format(money, language).replace(symbol, '').trim();
  return max.startsWith(symbol)
    ? `${symbol}${number(range.min)}–${number(range.max)}`
    : `${number(range.min)}–${max}`;
}

/** The currency's sign in the UI language: `€`, `US$`, `¥`. */
export function currencySymbol(currency: string, language: string): string {
  return new Intl.NumberFormat(localeOf(language), { style: 'currency', currency })
    .formatToParts(0)
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');
}

/** A currency's name in the UI language: `Euro`, `euro`; the code when it has none. */
export function currencyName(currency: string, language: string): string {
  return (
    new Intl.DisplayNames([language], { type: 'currency', fallback: 'none' }).of(currency) ??
    currency
  );
}

/** The text an amount field starts with: `12.5` → `12.50`, whole → `12`. */
export function amountText(minor: number, currency: string): string {
  const digits = digitsOf(currency);
  return minor % 10 ** digits === 0
    ? String(minor / 10 ** digits)
    : (minor / 10 ** digits).toFixed(digits);
}

/**
 * Reads what someone typed into minor units: `12`, `12.5`, `12,50`, `1 200` all work; `null`
 * for anything that is not an amount, or has more decimals than the currency.
 */
export function parseAmount(text: string, currency: string): number | null {
  const digits = digitsOf(currency);
  const cleaned = text.replace(/\s/g, '').replace(',', '.');
  const shape = digits === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`);
  if (!shape.test(cleaned)) {
    return null;
  }
  const [whole = '0', fraction = ''] = cleaned.split('.');
  return Number(whole) * 10 ** digits + Number(fraction.padEnd(digits, '0') || '0');
}

/** A currency's narrow sign alone: `€`, `$`, `¥`, `kr`; the code where it has none. */
function narrowSign(currency: string): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  })
    .formatToParts(0)
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');
}

/**
 * The amount a text says and the currency it says it in, as a confirmation prints a price: `€ 18`,
 * `18,00 EUR`, `฿1,200`, `kr 350`, `: EUR 110.30` as a label's line leaves it. A currency by its code, else by its sign among `offered` — a
 * sign several share (`$`, `¥`, `kr`) the first offered that has it, none when none does. The
 * amount as typed, for {@link parseAmount}; `null` when the text is more than an amount.
 */
export function moneyIn(
  text: string,
  offered: string[],
): { amount: string; currency: string | null } | null {
  const found =
    /^([^\d]{0,8}?)\s*(\d[\d\s]*(?:[.,]\d{3})*(?:[.,]\d{1,2})?)\s*([^\d]{0,8})$/.exec(
      text.trim(),
    );
  if (!found) return null;
  const [, before = '', number = '', after = ''] = found;
  // What stands about it but for the colons and brackets a label leaves: `: EUR 110.30`.
  const marks = `${before} ${after}`.replace(/[:;=*()[\]–—-]/g, ' ').trim();
  const code = /\b[A-Z]{3}\b/.exec(marks)?.[0];
  const sign = marks.replace(/\b[A-Z]{3}\b/, '').trim();
  const currency =
    code ?? (sign === '' ? null : (offered.find((one) => narrowSign(one) === sign) ?? null));
  // Letters that are no currency's: a word with a number, not a price.
  if (marks !== '' && currency === null) return null;
  // The thousands' separators gone, its decimal one kept: `1,234.56`, `1.234,56`, `1 200`.
  const [head = '', cents] = number.split(/[.,](?=\d{1,2}$)/);
  const whole = head.replace(/[\s.,]/g, '');
  return { amount: cents === undefined ? whole : `${whole}.${cents}`, currency };
}

/**
 * `amount` minor units of `from` in minor units of `to`, at `perTrip` billionths of `from` to
 * one `to`: the estimate shown before the server converts it exactly.
 */
export function convert(amount: number, from: string, to: string, perTrip: number): number {
  const units = amount / 10 ** digitsOf(from) / (perTrip / 1e9);
  return Math.round(units * 10 ** digitsOf(to));
}

/**
 * `amount` minor units of the trip's money `from` in minor units of `to`, at `perTrip`
 * billionths of `to` to one `from`: a price planned in euros, seen in the country's money.
 */
export function convertBack(amount: number, from: string, to: string, perTrip: number): number {
  const units = (amount / 10 ** digitsOf(from)) * (perTrip / 1e9);
  return Math.round(units * 10 ** digitsOf(to));
}
